'use client';

import React from 'react';
import classnames from 'classnames';
import Typography from '../../../atoms/Typography/Typography';
import IconButton from '../../../atoms/IconButton/IconButton';
import Button from '../../../atoms/Button/Button';
import Box from '../../../atoms/Box/Box';
import styles from './Panel.module.scss';

type Theme = 'dark' | 'light';

export type PanelProps = {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback when the panel is closed */
  onClose: () => void;
  /** Panel title */
  title: string;
  /** Panel width */
  width?: string;
  /** Theme for the panel */
  theme?: Theme;
  /** Panel content */
  children: React.ReactNode;
  /** Callback when save button is clicked */
  onSave?: () => void;
  /** Label for the save button */
  saveLabel?: string;
  /** Whether to show the footer with save button (default: true) */
  showFooter?: boolean;
  /** Test ID prefix for the panel */
  dataTestId?: string;
  /** Additional class name */
  className?: string;
};

/**
 * A panel component that slides in from the right side of the table.
 * Unlike the Slider component, this panel is positioned relative to its
 * parent container (the table) rather than the viewport.
 *
 * @component
 * @param {PanelProps} props - Component props
 * @returns {React.ReactElement} The Panel component
 */
export const Panel: React.FC<PanelProps> = ({
  isOpen,
  onClose,
  title,
  width = '320px',
  theme = 'dark',
  children,
  onSave,
  saveLabel = 'Save',
  showFooter = true,
  dataTestId = 'table-panel',
  className,
}) => {
  return (
    <>
      {/* Overlay - only covers the table area */}
      <Box
        className={classnames(styles.overlay, { [styles.open]: isOpen })}
        onClick={onClose}
        data-theme={theme}
      />

      {/* Panel */}
      <Box
        className={classnames(styles.panel, { [styles.open]: isOpen }, className)}
        // eslint-disable-next-line react/forbid-component-props
        style={{ width }}
        data-theme={theme}
        data-testid={dataTestId}
      >
        {/* Header */}
        <Box className={styles.header}>
          <Typography variant="subtitle" size="small" theme={theme} color="white">
            {title}
          </Typography>
          <IconButton
            icon={{ category: 'global', name: 'close' }}
            size="small"
            theme={theme}
            buttonType="tertiary"
            gaComponent={`${dataTestId}-close`}
            dataTestId={`${dataTestId}-close-button`}
            handleClick={onClose}
          />
        </Box>

        {/* Body */}
        <Box className={styles.body}>
          {children}

          {/* Footer */}
          {showFooter && onSave && (
            <Box className={styles.footer}>
              <Button
                label={saveLabel}
                handleClick={onSave}
                type="primary"
                color="neutral"
                size="medium"
                gaComponent={`${dataTestId}-save`}
                dataTestId={`${dataTestId}-save-button`}
                isFluid={true}
                theme={theme}
              />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

Panel.displayName = 'Panel';
